import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Drawer } from "antd";
import { message } from "../../components/antdMessageBridge";
import { CloseCircleOutlined } from "@ant-design/icons";
import "../manifest_verificationqueue/verification-slider.css";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import CommonObj from "../../CommonObj";
import { useAuth } from "../../auth/AuthContext";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";

const VendorSlide = (props) => {
	const [iform, setIForm] = useState({
		displayname: "",
		status: "",
	});
	const { roleUserId } = useAuth();
	const { accessToken } = useAuthToken();
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});
	const [ierrors, setIErrors] = useState({});

	const [selectedStatus, setSelectedStatus] = useState();
	const [error, setError] = useState(null);
	const [vendorMasterList, setVendorMasterList] = useState([]);

	const resolveVendorMasterId = (record) => {
		if (!record) return undefined;
		return (
			record.isbnVendorMasterId ||
			record.vendorMasterId ||
			record.vendorId ||
			record.VendorId ||
			record.id
		);
	};

	const fetchUserData = async (vendorMasterId) => {
		setLoading(true);
		const requestData = {
			UserId: roleUserId,
			VendorId: vendorMasterId
		};

		let formData = new FormData();
		Object.keys(requestData).forEach(function (key) {
			formData.append(key, requestData[key]);
		});

		try {
			const myHeaders = {
				Authorization: `Bearer ${accessToken}`,
				"X-CSRF-Token": encryptTimestamp(),
			};
			const response = await fetch(
				CommonObj.Endpoints.GetISBNVendorMasterById,
				{
					method: "POST",
					headers: myHeaders,
					body: formData,
				}
			);

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}
			const responseText = await response.text();
			let result;
			result = JSON.parse(responseText);
			setIForm({
				displayname: result.vendorName || result.category || "",
				status: result.status || "",
			});
			setSelectedStatus(result.status);
		} catch (err) {
			setError(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!props.selectedRecord && props.visible) {
			setIForm({
				displayname: "",
				status: "",
			});
			setSelectedStatus(true);
		}
	}, [props.visible, props.selectedRecord]);

	useEffect(() => {
		if (props.selectedRecord && props.visible) {
			setIErrors({});
			const vendorMasterId = resolveVendorMasterId(props.selectedRecord);
			if (!vendorMasterId) {
				message.error("Unable to load vendor details: Vendor ID is missing.");
				return;
			}
			fetchUserData(vendorMasterId);
		}
	}, [props.selectedRecord, props.visible]);

	useEffect(() => {
		const fetchVendorMaster = async () => {
			if (!props.visible) return;

			try {
				const response = await fetch(CommonObj.Endpoints.GetISBNVendorMaster, {
					method: "GET",
					headers: {
						Authorization: `Bearer ${accessToken}`,
						"X-CSRF-Token": encryptTimestamp(),
						"Content-Type": "application/json",
					},
				});

				if (!response.ok) {
					throw new Error("Failed to fetch ISBN vendor master list");
				}

				const result = await response.json();
				setVendorMasterList(Array.isArray(result) ? result : [result]);
			} catch (err) {
				setVendorMasterList([]);
			}
		};

		fetchVendorMaster();
	}, [props.visible, accessToken]);

	const handleInternalSubmit = async (e) => {
		e.preventDefault();

		if (!i_validate()) {
			focusFirstError(ierrors);
			return;
		}

		setLoading(true);
		try {
			const isEdit = !!props.selectedRecord;
			const formData = getRequestFormData(isEdit);
			const endpoint = isEdit
				? CommonObj.Endpoints.UpdateISBNVendorMaster
				: CommonObj.Endpoints.CreateISBNVendorMaster;

			const response = await fetch(endpoint, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${accessToken}`,
					"X-CSRF-Token": encryptTimestamp(),
				},
				body: formData,
			});

			if (!response.ok) throw new Error(`Status: ${response.status}`);

			const result = await response.json();
			processApiResponse(result, isEdit);
		} catch (error) {
			message.error(`Error submitting form: ${error.message}`);
		} finally {
			setLoading(false);
		}
	};

	const focusFirstError = (errors) => {
		const firstField = Object.keys(errors)[0];
		const element = document.getElementById(firstField);
		if (element) element.focus();
	};

	const getRequestFormData = (isEdit) => {
		const formData = new FormData();
		const payload = {
			VendorName: iform.displayname,
			UserId: roleUserId,
			...(isEdit && {
				ISBNVendorMasterId: resolveVendorMasterId(props.selectedRecord),
				Status: selectedStatus ? 1 : 0,
			}),
		};
		Object.entries(payload).forEach(([k, v]) => formData.append(k, v));
		return formData;
	};

	const processApiResponse = (result, isEdit) => {
		if (result?.status !== true) {
			return message.error("Vendor already exists.");
		}

		if (result) {
			message.success(
				`Vendor ${isEdit ? "updated" : "created"} successfully.`
			);
			handleClear();
			props.onClose();
			props.onSuccess?.();
			return;
		}

		message.error(result?.message || "Failed to save the data");
	};

	const handleClear = () => {
		setSelectedStatus(true);
		setSelectedStatus(false);
		setIForm({
			displayname: "",
		});
		setErrors({});
		setIErrors({});
	};

	const i_validate = () => {
		const newErrors = {};

		if (!iform.displayname?.trim()) {
			newErrors.displayname = "Please enter Vendor Name";
		}

		if (!props.selectedRecord) {
			const exists = vendorMasterList.some(
				(item) =>
					(item.vendorName || item.category || "")
						.toLowerCase()
						.trim() === iform.displayname.toLowerCase().trim()
			);
			if (exists) {
				newErrors.displayname = "Vendor already exists";
			}
		}

		setIErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleInternalChange = (e) => {
		const { name, value } = e.target;
		setIForm((prev) => ({ ...prev, [name]: value }));
		if (ierrors[name]) {
			setIErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	const handlestatusChange = (e) => {
		setSelectedStatus(e.target.checked);
	};

	useEffect(() => {
		if (props.selectedRecord) {
			message.info(props.selectedRecord);
		}
		setIForm({
			displayname: "",
			status: "",
		});
	}, []);

	const handleClose = () => {
		setIErrors({});
		props.onClose();
	};

	return (
		<Drawer
			title={props.selectedRecord ? "Edit Vendor" : props.title}
			placement={props.placement}
			onClose={handleClose}
			open={props.visible}
			width={props.width}
			className={props.className}
			closeIcon={<CloseCircleOutlined />}
			loading={loading}
		>
			<div className="modal-body">
				<div className="">
					<form
						className="book-page-form"
						onSubmit={handleInternalSubmit}
						noValidate
					>
						<div className="add-user-internal-container">
							<div className="field-group">
								<input
									id="displayname"
									name="displayname"
									className={`${"book-page-form-input-text"} ${iform?.displayname === "" ? "" : "has-text"
										}`}
									maxLength={100}
									value={iform.displayname || ""}
									onChange={(e) => {
										const value = e.target.value;
										handleInternalChange({
											target: { name: "displayname", value: value },
										});
									}}
									required
								/>
								<label htmlFor="displayname" className="label">
									Vendor Name <span className="form-required">*</span>
								</label>
								{ierrors.displayname && (
									<span className="error-msg">{ierrors.displayname}</span>
								)}
							</div>
							{props?.selectedRecord && (
								<div className="book-page-form-checkbox-button">
									<input
										type="checkbox"
										id="status"
										name="status"
										data-track="vendor-master-edit-is-active-toggle"
										onChange={handlestatusChange}
										checked={selectedStatus || undefined}
									/>
									<label htmlFor="used-condition-content">Is Active</label>
									<span className="error-msg">{errors.status}</span>
								</div>
							)}
						</div>
						<div className="modal-footer">
							<button className="modal-cancel-button" onClick={handleClear}>
								Clear
							</button>
							<button className="accept-button" data-track="vendor-master-submit-vendor">Submit</button>
						</div>
					</form>
				</div>
			</div>
		</Drawer>
	);
};

VendorSlide.propTypes = {
	visible: PropTypes.bool.isRequired,
	title: PropTypes.string,
	placement: PropTypes.oneOf(["right", "left", "top", "bottom"]),
	width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	className: PropTypes.string,
	selectedRecord: PropTypes.shape({
		isbnVendorMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		vendorMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
			.isRequired,
	}),
	onClose: PropTypes.func.isRequired,
	onSuccess: PropTypes.func,
};

VendorSlide.defaultProps = {
	title: "Add Vendor",
	placement: "right",
	width: 400,
	className: "",
	selectedRecord: null,
	onSuccess: undefined,
};

export default VendorSlide;